import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDate, LevelBadge } from "@/lib/ui";
import { Input } from "@/components/ui/input";
import { Search as SearchIcon, Boxes, FileText, Bell } from "lucide-react";

export default function Recherche() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (val) => {
    const query = val ?? q;
    if (!query.trim()) { setRes(null); return; }
    setBusy(true);
    try { const { data } = await api.get(`/search?q=${encodeURIComponent(query)}`); setRes(data); }
    finally { setBusy(false); }
  };

  const total = res ? res.items.length + res.documents.length + res.reminders.length : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Recherche universelle</h1>
        <p className="text-muted-foreground text-sm mt-1">Objets, documents, événements, contrats — tout ta mémoire OMY au même endroit.</p>
      </div>

      <div className="omy-glass rounded-2xl flex items-center gap-3 px-4 py-1">
        <SearchIcon size={18} className="text-muted-foreground" />
        <Input data-testid="search-input" value={q} onChange={(e) => { setQ(e.target.value); run(e.target.value); }}
          placeholder="clim, Samsung, facture TV, pneus, Leroy Merlin…"
          className="border-0 bg-transparent focus-visible:ring-0 text-base" />
      </div>

      {busy && <p className="text-muted-foreground text-sm">Recherche…</p>}
      {res && total === 0 && <p className="text-muted-foreground text-sm">Aucun résultat pour « {q} ».</p>}

      {res && res.items.length > 0 && (
        <section data-testid="search-items">
          <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Biens ({res.items.length})</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {res.items.map((it) => (
              <button key={it.id} onClick={() => navigate(`/app/memoire/${it.id}`)}
                className="omy-glass rounded-xl p-4 text-left flex items-center gap-3 hover:border-primary/40 transition-all">
                <Boxes size={18} className="text-primary" />
                <div><p className="text-sm font-medium">{it.name}</p>
                  <p className="text-xs text-muted-foreground">{it.brand} {it.model}</p></div>
              </button>
            ))}
          </div>
        </section>
      )}

      {res && res.documents.length > 0 && (
        <section data-testid="search-documents">
          <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Documents ({res.documents.length})</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {res.documents.map((d) => (
              <div key={d.id} className="omy-glass rounded-xl p-4 flex items-center gap-3">
                <FileText size={18} className="text-primary" />
                <div><p className="text-sm font-medium">{d.original_filename}</p>
                  <p className="text-xs text-muted-foreground">{d.doc_type} · {fmtDate(d.created_at)}</p></div>
              </div>
            ))}
          </div>
        </section>
      )}

      {res && res.reminders.length > 0 && (
        <section data-testid="search-reminders">
          <h2 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Rappels ({res.reminders.length})</h2>
          <div className="space-y-2">
            {res.reminders.map((r) => (
              <div key={r.id} className="omy-glass rounded-xl p-4 flex items-center justify-between">
                <span className="flex items-center gap-3 text-sm"><Bell size={16} className="text-accent" /> {r.title}</span>
                <LevelBadge level={r.level} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
